<template>
    <div
        class="ppv2-flex-1 ppv2-flex ppv2-flex-col ppv2-items-center ppv2-justify-center"
        :style="{ gap: normalizeSize(14, true) }"
    >
        <p
            v-if="horaires_specifiques.length == 0"
            class="!ppv2-mb-0 ppv2-text-black"
            :style="{ fontSize: normalizeFontSize(12) }"
        >
            {{ $t('experience.step4.specificMode.emptyDisponibility') }}
        </p>
        <transition name="fade">
            <div style="width: 100%">
                <table
                    v-if="horaires_specifiques.length > 0"
                    class="ppv2-w-full text-left ppv2-text-black"
                    :style="{ fontSize: '11px' }"
                >
                    <thead>
                        <tr class="py-1 space-x-2">
                            <th class="poppinsMedium" :style="{ paddingLeft: normalizeSize(27, true) }">
                                {{ $t('experience.step5.specificMode.dates') }}
                            </th>
                            <th class="poppinsMedium">{{ $t('configuration.cards.schedule.title') }}</th>
                            <th class="poppinsMedium text-right" :style="{ paddingRight: normalizeSize(27, true) }"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="(horaire, index) in horaires_specifiques"
                            :key="index"
                            class="evenItem poppinsLight ppv2-text-black ppv2-py-1"
                            :style="{ fontSize: '11px' }"
                        >
                            <td
                                class="py-1"
                                :style="{ paddingLeft: normalizeSize(27, true), maxWidth: normalizeSize(250, true) }"
                            >
                                <div class="poppinsMedium">{{ horaire.label }}</div>
                                <small class="italic">{{ formatDays(horaire) }}</small>
                            </td>
                            <td class="py-1">{{ horaire.hours }}</td>
                            <td class="text-right space-x-2 py-1" :style="{ paddingRight: normalizeSize(27, true) }">
                                <SquarePen class="ppv2-cursor-pointer ppv2-ml-2" :size="15" @click="editTarif(horaire, index)" />
                                <CopyPlus
                                    class="ppv2-cursor-pointer ppv2-ml-2"
                                    :size="15"
                                    @click="initDuplicate(horaire, index)"
                                />
                                <Trash2 class="ppv2-cursor-pointer" :size="15" @click="$emit('deleteSpecH', index)" />
                            </td>
                        </tr>
                    </tbody>
                </table>
                <div class="text-center" :class="{ 'mt-4': horaires_specifiques.length > 0 }">
                    <!-- Add :key to force new component when adding -->
                    <BaseButton id="button" type="button" variant="secondary" @click="addDisponibility" :customFontSize="12">
                        <Plus :size="12" />
                        <span>{{ $t('experience.step4.specificMode.addDisponibility') }}</span>
                    </BaseButton>
                </div>
            </div>
        </transition>

        <!-- Add :key to force re-render when adding new (not when editing) -->
        <TimeSpecific
            v-model="showDialog"
            :editing-data="editingTarif"
            :titlePopup="$t('experience.step4.specificMode.disponibilityTitle')"
            :defaultTitle="dialogTitle"
            :subCategoryId="subCategoryId"
            :establishmentId="establishmentId"
            :prestation="prestation"
            :horaires_specifiques="horaires_specifiques"
            @save="handleSave"
            @confirm="handleConfirm"
            @cancel="handleCancel"
            :key="dialogKey"
            :editing-index="editingIndex"
        />
    </div>
</template>

<script>
import normalizeSize from '../../../../utils/normalizeSize'
import { CopyPlus, SquarePen, Trash2, Plus } from 'lucide-vue'
import BaseButton from '../../../Shared/BaseButton.vue'
import normalizeSizeVertical from '../../../../utils/normalizeSizeVertical'
import TimeSpecific from './TimeSpecific.vue'
import normalizeFontSize from '../../../../utils/normalizeFontSize'

export default {
    name: 'ListHoraireSpecifique',
    components: { BaseButton, SquarePen, Trash2, CopyPlus, Plus, TimeSpecific },
    props: {
        horaires_specifiques: {
            type: Array,
            required: true,
        },
        subCategoryId: {
            type: [Number, String],
            default: null,
        },
        establishmentId: {
            type: [Number, String],
            default: null,
        },
        prestation: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            showTimeSpecific: false,
            editingTarif: null,
            editingIndex: -1,
            showDialog: false,
            dialogKey: 0, // This will change when adding new
            isAddingNew: false, // Track if we're adding or editing
            duplicateTargetIndex: -1,
        }
    },
    computed: {
        dialogTitle() {
            return this.isAddingNew
                ? this.$t('experience.step4.specificMode.popupTitleSection')
                : this.$t('experience.step4.specificMode.editDisponibility')
        },
    },
    methods: {
        normalizeFontSize,
        normalizeSizeVertical,
        normalizeSize,

        // Method called when "Add Disponibility" button is clicked
        addDisponibility() {
            this.isAddingNew = true
            this.editingTarif = null
            this.editingIndex = -1
            this.duplicateTargetIndex = -1
            this.dialogKey += 1 // Change the key to force re-render
            this.showDialog = true
        },

        initDuplicate(horaire, index) {
            this.isAddingNew = true
            this.editingTarif = {
                ...horaire,
                selectedDays: horaire.selectedDays,
                tmp_id: Date.now(), // Generate new ID
            }
            // For duplication, we act as if we are adding new (validation against all existing)
            this.editingIndex = -1
            this.duplicateTargetIndex = index
            this.dialogKey += 1
            this.showDialog = true
        },

        // Method called when editing an item
        editTarif(tarif, index) {
            this.isAddingNew = false
            this.editingTarif = {
                ...tarif,
                selectedDays: tarif.selectedDays,
            }
            this.editingIndex = index
            this.duplicateTargetIndex = -1
            // Don't change dialogKey when editing, so form keeps data
            this.showDialog = true
        },

        handleSave(data) {
            console.log('Disponibilité enregistrée:', data)

            const newTarif = {
                ...data,
                label: !data.dateEnd
                    ? `${this.$t('common.from')} ${this.formatDateFr(data.dateStart)}`
                    : `${this.$t('common.from')} ${this.formatDateFr(data.dateStart)} ${this.$t(
                          'common.to_date'
                      )} ${this.formatDateFr(data.dateEnd)}`,
                hours: data.timeStart + ' – ' + data.timeEnd,
                label_days: this.getDaysBetween(data.dateStart, data.dateEnd),
            }

            if (this.editingIndex >= 0) {
                this.$emit('updateSpeH', this.editingIndex, newTarif)
            } else if (this.duplicateTargetIndex >= 0) {
                this.$emit('duplicateHoraireSpec', newTarif, this.duplicateTargetIndex)
            } else {
                this.$emit('addSpeH', newTarif)
            }

            // Reset after saving
            this.resetEditing()
        },

        handleCancel() {
            this.resetEditing()
        },

        resetEditing() {
            this.editingTarif = null
            this.editingIndex = -1
            this.duplicateTargetIndex = -1
            this.isAddingNew = false
            this.showDialog = false
        },

        handleConfirm() {
            console.log('Confirmed!')
        },

        formatDate(dateValue) {
            if (!dateValue) return ''

            if (typeof dateValue === 'string') {
                const [year, month, day] = dateValue.split('-')
                const dateObj = new Date(year, month - 1, day)
                const options = { day: 'numeric', month: 'long', year: 'numeric' }
                return dateObj.toLocaleDateString(this.$i18n.locale === 'fr' ? 'fr-FR' : 'en-US', options)
            }

            if (dateValue instanceof Date) {
                const options = { day: 'numeric', month: 'long', year: 'numeric' }
                return dateValue.toLocaleDateString(this.$i18n.locale === 'fr' ? 'fr-FR' : 'en-US', options)
            }

            return ''
        },

        formatDateFr(dateStr) {
            const options = { day: 'numeric', month: 'long', year: 'numeric' }
            return new Date(dateStr).toLocaleDateString(this.$i18n.locale === 'fr' ? 'fr-FR' : 'en-US', options)
        },

        getDaysBetween(startDate, endDate) {
            const weekdays = [
                this.$t('resource_planning.week_days.sunday'),
                this.$t('resource_planning.week_days.monday'),
                this.$t('resource_planning.week_days.tuesday'),
                this.$t('resource_planning.week_days.wednesday'),
                this.$t('resource_planning.week_days.thursday'),
                this.$t('resource_planning.week_days.friday'),
                this.$t('resource_planning.week_days.saturday'),
            ]

            // No end date: return all weekdays
            if (!endDate) {
                return weekdays.slice(1).concat(weekdays[0]).join(', ')
            }

            const days = []
            let currentDate = new Date(startDate)
            const end = new Date(endDate)

            while (currentDate <= end) {
                days.push(weekdays[currentDate.getDay()])
                currentDate.setDate(currentDate.getDate() + 1)
            }

            // Remove duplicates (if same day appears multiple times)
            return [...new Set(days)].join(', ')
        },

        formatDays(horaire) {
            const raw = horaire.selectedDays
            if (!Array.isArray(raw) || raw.length === 0) return ''

            const daysArray = raw
                .flatMap((d) => (typeof d === 'string' ? d.split(',').map((s) => s.trim()) : [d]))
                .filter(Boolean)
            if (daysArray.length === 0) return ''

            const allDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
            const weekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']
            const weekendDays = ['Saturday', 'Sunday']

            const selected = new Set(daysArray)

            if (daysArray.length === 7) return this.$t('common.allDays')

            if (weekDays.every((d) => selected.has(d)) && weekendDays.every((d) => !selected.has(d))) {
                return this.$t('common.weekDays')
            }

            if (weekendDays.every((d) => selected.has(d)) && weekDays.every((d) => !selected.has(d))) {
                return this.$t('common.weekendDays')
            }

            const missingDays = allDays.filter((d) => !selected.has(d))
            if (missingDays.length > 0 && missingDays.length <= 2 && daysArray.length >= 5) {
                const exceptList = missingDays.map((d) => this.$t(`resource_planning.week_days.${d.toLowerCase()}`))
                if (exceptList.length === 1) return `${this.$t('common.allDaysExpect')} ${exceptList[0]}`
                return `${this.$t('common.allDaysExpect')} ${exceptList.join(` ${this.$t('common.and')} `)}`
            }

            const dayNames = daysArray.map((d) => this.$t(`resource_planning.week_days.${d.toLowerCase()}`))

            if (dayNames.length === 1) {
                if (horaire.dateStart && horaire.dateEnd && horaire.dateStart === horaire.dateEnd) {
                    return dayNames[0]
                }
                return `${this.$t('common.allSpecificDay')} ${dayNames[0]}`
            }

            return `${this.$t('common.allSpecificDay')} ${dayNames.slice(0, -1).join(', ')} ${this.$t(
                'common.and'
            )} ${dayNames.slice(-1)}`
        },
    },

    watch: {
        // Reset when dialog closes
        showDialog(newVal) {
            if (!newVal) {
                this.resetEditing()
            }
        },
    },
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}
.fade-enter,
.fade-leave-to {
    opacity: 0;
}
</style>
